<template>
    <div class="speech-settings-tab">
        <div class="tab-header">
            <h3>{{ $t("voiceagent.speechSettingsTitle") }}</h3>
            <p>{{ $t("voiceagent.speechSettingsSubtitle") }}</p>
        </div>

        <a-form layout="vertical" class="settings-form">
            <!-- Ses Ayarları -->
            <div class="settings-section">
                <div class="section-title">
                    <h4>{{ $t("voiceagent.backgroundSound") }}</h4>
                </div>

                <!-- Background Sound -->
                <a-form-item :label="$t('voiceagent.backgroundSound')">
                    <a-select v-model="formData.ambient_sound" style="width: 100%" @change="updateSettings">
                        <a-select-option
                            v-for="sound in backgroundSoundOptions"
                            :key="sound.value"
                            :value="sound.value"
                        >
                            {{ sound.label }}
                        </a-select-option>
                    </a-select>
                    <div class="description">
                        {{ $t("voiceagent.backgroundSoundDescription") }}
                    </div>
                </a-form-item>

                <a-form-item v-if="formData.ambient_sound" :label="$t('voiceagent.backgroundSoundLevel')">
                    <div class="slider-container">
                        <a-slider
                            v-model="formData.ambient_sound_volume"
                            :min="0"
                            :max="1"
                            :step="0.1"
                            @change="updateSettings"
                            class="volume-slider"
                        />
                        <a-input-number
                            v-model="formData.ambient_sound_volume"
                            @change="updateSettings"
                            :min="0"
                            :max="1"
                            :step="0.1"
                        />
                    </div>
                    <div class="description">{{ $t("voiceagent.backgroundSoundLevelDescription") }}</div>
                </a-form-item>
            </div>

            <!-- Konuşma ve Yanıt Davranışı -->
            <div class="settings-section">
                <div class="section-title">
                    <h4>{{ $t("voiceagent.speechBehavior") }}</h4>
                </div>

                <!-- Responsiveness -->
                <a-form-item :label="$t('voiceagent.responsiveness')">
                    <div class="slider-container">
                        <a-slider
                            v-model="formData.responsiveness"
                            :min="0"
                            :max="1"
                            :step="0.05"
                            @change="updateSettings"
                            class="responsive-slider"
                        />
                        <a-input-number 
                            v-model="formData.responsiveness" 
                            :min="0"
                            :max="1"
                            :step="0.05"
                            @change="updateSettings" 
                        />
                    </div>
                    <div class="description">
                        {{ $t("voiceagent.responsivenessDescription") }}
                    </div>
                </a-form-item>

                <!-- Interruption Sensitivity -->
                <a-form-item :label="$t('voiceagent.interruptionSensitivity')">
                    <div class="slider-container">
                        <a-slider
                            v-model="formData.interruption_sensitivity"
                            :min="0"
                            :max="1"
                            :step="0.05"
                            @change="updateSettings"
                            class="sensitivity-slider"
                        />
                        <a-input-number 
                            v-model="formData.interruption_sensitivity" 
                            :min="0"
                            :max="1"
                            :step="0.05"
                            @change="updateSettings" 
                        />
                    </div>
                    <div class="description">
                        {{ $t("voiceagent.interruptionSensitivityDescription") }}
                    </div>
                </a-form-item>
            </div>

            <!-- Backchannel Ayarları -->
            <div class="settings-section">
                <div class="section-title">
                    <h4>{{ $t("voiceagent.backchannelSection") }}</h4>
                    <div class="description">
                        {{ $t("voiceagent.backchannelDescription") }}
                    </div>
                </div>

                <!-- Enable Backchanneling -->
                <a-form-item :label="$t('voiceagent.backchannelEnable')">
                    <a-switch v-model="formData.enable_backchannel" @change="updateSettings" />
                    <div class="description">{{ $t("voiceagent.backchannelEnableDescription") }}</div>
                </a-form-item>

                <!-- Backchannel Settings (if enabled) -->
                <div v-if="formData.enable_backchannel" class="backchannel-settings">
                    <!-- Backchannel Frequency -->
                    <a-form-item :label="$t('voiceagent.backchannelFrequency')">
                        <div class="slider-container">
                            <a-slider
                                v-model="formData.backchannel_frequency"
                                :min="0"
                                :max="1"
                                :step="0.1"
                                @change="updateSettings"
                                class="frequency-slider"
                            />
                            <div class="slider-value">{{ formData.backchannel_frequency }}</div>
                        </div>
                        <div class="description">
                            {{ $t("voiceagent.backchannelFrequencyDescription") }}
                        </div>
                    </a-form-item>

                    <!-- Backchannel Words -->
                    <a-form-item :label="$t('voiceagent.backchannelWords')">
                        <a-select
                            v-model="formData.backchannel_words"
                            mode="tags"
                            style="width: 100%"
                            :placeholder="$t('voiceagent.backchannelWordsPlaceholder')"
                            @change="updateSettings"
                        >
                            <a-select-option v-for="word in defaultBackchannelWords" :key="word" :value="word">
                                {{ word }}
                            </a-select-option>
                        </a-select>
                        <div class="description">
                            {{ $t("voiceagent.backchannelWordsDescription") }}
                        </div>
                    </a-form-item>
                </div>
            </div>

            <!-- Gürültü Azaltma Ayarları -->
            <div class="settings-section">
                <div class="section-title">
                    <h4>{{ $t("voiceagent.noiseReductionSettings") }}</h4>
                    <div class="description">
                        {{ $t("voiceagent.denoisingSectionDescription") }}
                    </div>
                </div>

                <!-- Denoising Mode -->
                <a-form-item :label="$t('voiceagent.denoisingMode')">
                    <div class="radio-group-container">
                        <a-radio-group v-model="formData.denoising_mode" @change="updateSettings">
                            <div class="radio-option">
                                <div class="radio-header">
                                    <a-radio value="noise-cancellation">
                                        <span class="radio-label">{{ $t("voiceagent.noiseCancellation") }}</span>
                                    </a-radio>
                                </div>
                                <div class="radio-description">{{ $t("voiceagent.noiseCancellationDesc") }}</div>
                            </div>
                            <div class="radio-option">
                                <div class="radio-header">
                                    <a-radio value="noise-and-background-speech-cancellation">
                                        <span class="radio-label">{{ $t("voiceagent.noiseAndBackgroundSpeechCancellation") }}</span>
                                    </a-radio>
                                    <a-tooltip placement="right" :title="$t('voiceagent.denoisingTvSpeechTooltip')">
                                        <i class="material-icons info-icon">info_outline</i>
                                    </a-tooltip>
                                </div>
                                <div class="radio-description">{{ $t("voiceagent.noiseAndBackgroundSpeechCancellationDesc") }}</div>
                            </div>
                        </a-radio-group>
                    </div>
                </a-form-item>
            </div>

            <!-- Konuşma ve Transkripsiyon Ayarları -->
            <div class="settings-section">
                <div class="section-title">
                    <h4>{{ $t("voiceagent.transcriptSettings") }}</h4>
                </div>

                <!-- Transcription Mode -->
                <a-form-item :label="$t('voiceagent.sttMode')">
                    <div class="radio-group-container">
                        <a-radio-group v-model="formData.stt_mode" @change="updateSettings">
                            <div class="radio-option">
                                <div class="radio-header">
                                    <a-radio value="fast">
                                        <span class="radio-label">{{ $t("voiceagent.sttOptimizeFast") }}</span>
                                    </a-radio>
                                </div>
                                <div class="radio-description">{{ $t("voiceagent.sttOptimizeFastDesc") }}</div>
                            </div>
                            <div class="radio-option">
                                <div class="radio-header">
                                    <a-radio value="accurate">
                                        <span class="radio-label">{{ $t("voiceagent.sttOptimizeAccurate") }}</span>
                                    </a-radio>
                                    <a-tooltip placement="right" :title="$t('voiceagent.sttAccurateTooltip')">
                                        <i class="material-icons info-icon">info_outline</i>
                                    </a-tooltip>
                                </div>
                                <div class="radio-description">{{ $t("voiceagent.sttOptimizeAccurateDesc") }}</div>
                            </div>
                        </a-radio-group>
                    </div>
                </a-form-item>

                <!-- Boosted Keywords -->
                <a-form-item :label="$t('voiceagent.boostedKeywords')">
                    <a-select
                        v-model="formData.boosted_keywords"
                        mode="tags"
                        style="width: 100%"
                        :placeholder="$t('voiceagent.boostedKeywordsPlaceholder')"
                        @change="updateSettings"
                    />
                    <div class="description">
                        {{ $t("voiceagent.boostedKeywordsDescription") }}
                    </div>
                </a-form-item>

                <!-- Enable Speech Normalization -->
                <a-form-item :label="$t('voiceagent.enableSpeechNormalization')">
                    <div class="switch-container">
                        <a-switch v-model="formData.normalize_for_speech" @change="updateSettings" />
                        <a-tooltip placement="right" :title="$t('voiceagent.speechNormTooltip')">
                            <i class="material-icons info-icon">info_outline</i>
                        </a-tooltip>
                    </div>
                    <div class="description">
                        {{ $t("voiceagent.enableSpeechNormalizationDescription") }}
                    </div>
                </a-form-item>

                <!-- Enable Transcript Formatting -->
                <a-form-item :label="$t('voiceagent.enableTranscriptFormatting')">
                    <a-switch v-model="formData.enable_transcription_formatting" @change="updateSettings" />
                    <div class="description">
                        {{ $t("voiceagent.enableTranscriptFormattingDescription") }}
                    </div>
                </a-form-item>
            </div>

            <!-- Hatırlatma Mesajı Ayarları -->
            <div class="settings-section">
                <div class="section-title">
                    <h4>{{ $t("voiceagent.reminderMessageSettings") }}</h4>
                </div>

                <!-- Reminder Frequency -->
                <a-form-item :label="$t('voiceagent.reminderTriggerMs')">
                    <a-input-number
                        v-model="formData.reminder_trigger_ms"
                        :min="5000"
                        :max="60000"
                        :step="1000"
                        style="width: 100%"
                        @change="updateSettings"
                    />
                    <div class="description">
                        {{ $t("voiceagent.reminderTriggerMsDescription") }}
                    </div>
                </a-form-item>

                <!-- Reminder Max Count -->
                <a-form-item :label="$t('voiceagent.maxReminders')">
                    <a-input-number
                        v-model="formData.reminder_max_count"
                        :min="0"
                        :max="5"
                        :step="1"
                        style="width: 100%"
                        @change="updateSettings"
                    />
                    <div class="description">{{ $t("voiceagent.maxRemindersDescription") }}</div>
                </a-form-item>
            </div>

            <!-- Telaffuz Sözlüğü -->
            <div class="settings-section">
                <div class="section-title">
                    <h4>{{ $t("voiceagent.pronunciationDictionary") }}</h4>
                    <div class="description">
                        {{ $t("voiceagent.pronunciationDictionaryHelp") }}
                    </div>
                </div>

                <div v-for="(item, index) in formData.pronunciation_dictionary" :key="index" class="pronunciation-item">
                    <div class="pronunciation-row">
                        <a-input v-model="item.word" :placeholder="$t('voiceagent.word')" style="width: 30%" @change="updateSettings" />
                        <a-select v-model="item.alphabet" style="width: 20%" @change="updateSettings">
                            <a-select-option value="ipa">IPA</a-select-option>
                            <a-select-option value="cmu">CMU</a-select-option>
                        </a-select>
                        <a-input
                            v-model="item.phoneme"
                            :placeholder="$t('voiceagent.phoneme')"
                            style="width: 40%"
                            @change="updateSettings"
                        />
                        <a-button type="danger" icon="delete" @click="removePronunciation(index)" style="width: 10%" />
                    </div>
                </div>

                <div class="add-pronunciation">
                    <a-button type="dashed" block @click="addPronunciation">
                        <i class="material-icons">add</i> {{ $t("voiceagent.addPronunciation") }}
                    </a-button>
                </div>
            </div>

            <!-- Kaydet Butonu -->
            <div class="form-actions">
                <PermissionController :permissions="['voice-agent.update']">
                    <a-button type="primary" @click="saveSettings" :loading="isSaving">
                        {{ $t("voiceagent.saveChangesButton") }}
                    </a-button>
                </PermissionController>
            </div>
        </a-form>
    </div>
</template>

<script>
import _ from "lodash";

export default {
    name: "SpeechSettingsTab",
    props: {
        agent: {
            type: Object,
            default: () => null,
        },
    },
    data() {
        return {
            formData: {
                ambient_sound: null,
                ambient_sound_volume: 1,
                responsiveness: 1,
                interruption_sensitivity: 1,
                enable_backchannel: false,
                backchannel_frequency: 0.5,
                backchannel_words: [],
                denoising_mode: "noise-cancellation",
                stt_mode: "fast",
                boosted_keywords: [],
                normalize_for_speech: true,
                enable_transcription_formatting: true,
                reminder_trigger_ms: 10000,
                reminder_max_count: 2,
                pronunciation_dictionary: [],
            },
            defaultBackchannelWords: ["evet", "hı hı", "anlıyorum", "tamam", "ilginç"],
            isSaving: false,
            debounceUpdate: null,
        };
    },
    computed: {
        backgroundSoundOptions() {
            return [
                { value: null, label: this.$t("voiceagent.backgroundSoundNone") },
                { value: "coffee-shop", label: this.$t("voiceagent.backgroundSoundCoffeeShop") },
                { value: "convention-hall", label: this.$t("voiceagent.backgroundSoundConventionHall") },
                { value: "summer-outdoor", label: this.$t("voiceagent.backgroundSoundSummerOutdoor") },
                { value: "mountain-outdoor", label: this.$t("voiceagent.backgroundSoundMountainOutdoor") },
                { value: "static-noise", label: this.$t("voiceagent.backgroundSoundStaticNoise") },
                { value: "call-center", label: this.$t("voiceagent.backgroundSoundCallCenter") },
            ];
        },
    },
    watch: {
        agent: {
            handler(newValue) {
                if (newValue) {
                    this.initializeFormData();
                }
            },
            immediate: true,
            deep: true,
        },
    },
    created() {
        // Debounce fonksiyonu oluşturma
        this.debounceUpdate = _.debounce(this.triggerUpdate, 1000);

        if (this.agent) {
            this.initializeFormData();
        }
    },
    methods: {
        initializeFormData() {
            // Agent'dan verileri formData'ya aktar
            this.formData.ambient_sound = this.agent.ambient_sound || null;
            this.formData.ambient_sound_volume =
                this.agent.ambient_sound_volume !== undefined ? this.agent.ambient_sound_volume : 1;
            this.formData.responsiveness = this.agent.responsiveness !== undefined ? this.agent.responsiveness : 1;
            this.formData.interruption_sensitivity =
                this.agent.interruption_sensitivity !== undefined ? this.agent.interruption_sensitivity : 1;
            this.formData.enable_backchannel = this.agent.enable_backchannel || false;
            this.formData.backchannel_frequency =
                this.agent.backchannel_frequency !== undefined ? this.agent.backchannel_frequency : 0.5;
            this.formData.backchannel_words = this.agent.backchannel_words || [];
            this.formData.denoising_mode = this.agent.denoising_mode || "noise-cancellation";
            this.formData.stt_mode = this.agent.stt_mode || "fast";
            this.formData.boosted_keywords = this.agent.boosted_keywords || [];
            this.formData.normalize_for_speech =
                this.agent.normalize_for_speech !== undefined ? this.agent.normalize_for_speech : true;
            this.formData.enable_transcription_formatting =
                this.agent.enable_transcription_formatting !== undefined
                    ? this.agent.enable_transcription_formatting
                    : true;
            this.formData.reminder_trigger_ms = this.agent.reminder_trigger_ms || 10000;
            this.formData.reminder_max_count =
                this.agent.reminder_max_count !== undefined ? this.agent.reminder_max_count : 2;
            this.formData.pronunciation_dictionary = Array.isArray(this.agent.pronunciation_dictionary)
                ? [...this.agent.pronunciation_dictionary]
                : [];

            // Eğer pronunciation_dictionary boşsa, varsayılan değeri ata
            if (this.formData.pronunciation_dictionary.length === 0) {
                this.formData.pronunciation_dictionary = [];
            }
        },
        updateSettings() {
            // Değişiklikleri toplu olarak göndermek için debounce kullan
            this.debounceUpdate();
        },
        triggerUpdate() {
            // Değişiklikleri parent komponente bildir
            // console.log("Değişiklikler:", this.formData);
        },
        saveSettings() {
            this.isSaving = true;

            // Ambient sound none ise null olarak gönder
            const updateData = { ...this.formData };

            // Pronunciation sözlüğü boşsa null olarak gönder
            if (updateData.pronunciation_dictionary.length === 0) {
                updateData.pronunciation_dictionary = null;
            }

            // Agent verisini güncelle
            this.$emit("update", updateData);

            setTimeout(() => {
                this.isSaving = false;
            }, 1000);
        },
        addPronunciation() {
            this.formData.pronunciation_dictionary.push({
                word: "",
                alphabet: "ipa",
                phoneme: "",
            });
        },
        removePronunciation(index) {
            this.formData.pronunciation_dictionary.splice(index, 1);
            this.updateSettings();
        },
    },
};
</script>

<style scoped lang="scss">
.speech-settings-tab {
    padding: 20px 0;

    .tab-header {
        margin-bottom: 20px;

        h3 {
            margin: 0 0 10px 0;
            font-weight: 500;
        }

        p {
            color: #666;
            margin: 0;
        }
    }

    .settings-form {
        max-width: 800px;
    }

    .settings-section {
        background-color: #f9f9f9;
        padding: 15px;
        border-radius: 4px;
        margin-bottom: 20px;
        border: 1px solid #e8e8e8;

        .section-title {
            margin-bottom: 15px;

            h4 {
                margin: 0 0 5px 0;
                font-weight: 500;
            }
        }
    }

    .radio-group-container {
        .radio-option {
            display: flex;
            flex-direction: column;
            margin-bottom: 16px;
            
            &:last-child {
                margin-bottom: 0;
            }

            .radio-header {
                display: flex;
                align-items: center;
                margin-bottom: 4px;

                .ant-radio-wrapper {
                    display: flex;
                    align-items: center;
                    margin-right: 0;
                }

                .radio-label {
                    font-weight: 500;
                    margin-left: 8px;
                }

                .info-icon {
                    color: #1890ff;
                    font-size: 16px;
                    margin-left: 8px;
                    cursor: help;
                    vertical-align: middle;
                }
            }

            .radio-description {
                color: #666;
                font-size: 12px;
                margin-left: 24px;
                line-height: 1.4;
            }
        }
    }

    .switch-container {
        display: flex;
        align-items: center;

        .info-icon {
            color: #1890ff;
            font-size: 16px;
            margin-left: 8px;
            cursor: help;
        }
    }

    .slider-container {
        display: flex;
        align-items: center;

        .volume-slider,
        .responsive-slider,
        .sensitivity-slider,
        .frequency-slider {
            flex: 1;
            margin-right: 15px;
            width: 100%;

            :deep(.ant-slider-rail) {
                background-color: #e1e1e1;
                height: 4px;
            }

            :deep(.ant-slider-track) {
                background-color: #1890ff;
                height: 4px;
            }

            :deep(.ant-slider-handle) {
                border: solid 2px #1890ff;
            }
        }

        .slider-value {
            width: 50px;
            text-align: right;
        }
    }

    .description {
        color: #666;
        font-size: 12px;
        margin-top: 5px;
    }

    .pronunciation-item {
        margin-bottom: 10px;

        .pronunciation-row {
            display: flex;
            gap: 10px;
        }
    }

    .add-pronunciation {
        margin-top: 15px;
    }

    .backchannel-settings {
        padding-left: 20px;
        border-left: 2px solid #f0f0f0;
        margin-top: 10px;
    }

    .form-actions {
        margin-top: 20px;
        text-align: right;
    }
}
</style>
